import { type ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { Modal } from '@/components/ui/modal';
import { Skeleton } from '@/components/ui/skeleton';
import { useRangeHistory, type PricingRange } from '@/hooks/use-pricing-ranges';

type Props = { rangeId: string | null; onClose: () => void };

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function EntryMeta({
  created,
  isCurrent,
}: {
  created: string;
  isCurrent: boolean;
}): ReactNode {
  return (
    <div className="mb-1.5 flex items-baseline gap-sm">
      <span className="text-[10px] uppercase tracking-wider text-text-tertiary">
        {formatDate(created)}
      </span>
      {isCurrent && (
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-brand-red">
          Vigente
        </span>
      )}
    </div>
  );
}

type HistoryEntryProps = { entry: PricingRange; isCurrent: boolean };

function TierLine({ tiers }: { tiers: PricingRange['tiers'] }): ReactNode {
  return (
    <div className="flex flex-wrap gap-base text-xs tabular-nums text-text-tertiary">
      {tiers.map((t, i) => (
        <span key={i}>
          {t.min}–{t.max ?? '∞'}:{' '}
          <span className="font-medium text-text-primary">
            ${t.price_per_lead}
          </span>
        </span>
      ))}
    </div>
  );
}

function HistoryEntry(p: HistoryEntryProps): ReactNode {
  const border = p.isCurrent ? 'border-brand-red' : 'border-white/[0.06]';
  return (
    <div className={cn('border-l-2 py-sm pl-md', border)}>
      <EntryMeta created={p.entry.created} isCurrent={p.isCurrent} />
      <TierLine tiers={p.entry.tiers} />
    </div>
  );
}

function Content({ rangeId }: { rangeId: string }): ReactNode {
  const { data, isLoading } = useRangeHistory(rangeId);
  if (isLoading) return <Skeleton className="h-32 rounded-lg" />;
  if (!data?.history.length) {
    return <p className="text-xs text-text-tertiary">No hay historial</p>;
  }
  return (
    <div className="space-y-md">
      {data.history.map((entry, i) => (
        <HistoryEntry key={entry.id} entry={entry} isCurrent={i === 0} />
      ))}
    </div>
  );
}

export function RangeHistoryModal({ rangeId, onClose }: Props): ReactNode {
  return (
    <Modal open={!!rangeId} onClose={onClose} title="Historial del rango">
      {rangeId && <Content rangeId={rangeId} />}
    </Modal>
  );
}
